import { clsx, type ClassValue } from 'clsx'

/**
 * Merge class names with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format a relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  return formatDate(d)
}

/**
 * Count words in text
 */
export function countWords(text: string): number {
  if (!text.trim()) return 0
  return text.trim().split(/\s+/).length
}

/**
 * Truncate text to a max length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Get step emoji for story steps
 */
export function getStepEmoji(stepType: string): string {
  const emojis: Record<string, string> = {
    beginning: '🌅',
    problem: '😰',
    adventure: '🚀',
    climax: '⚡',
    resolution: '🌈',
  }
  return emojis[stepType] || '📝'
}

/**
 * Get step title for story steps
 */
export function getStepTitle(stepType: string): string {
  const titles: Record<string, string> = {
    beginning: 'The Beginning',
    problem: 'The Problem',
    adventure: 'The Adventure',
    climax: 'The Climax',
    resolution: 'The Resolution',
  }
  return titles[stepType] || 'Story Step'
}

/**
 * Get default prompt hint for story steps
 */
export function getStepPrompt(stepType: string): string {
  const prompts: Record<string, string> = {
    beginning: 'How does your story start? Introduce your characters and setting.',
    problem: 'What problem or challenge does your hero face?',
    adventure: 'What happens as your hero tries to solve the problem?',
    climax: 'What is the most exciting moment? How does everything come together?',
    resolution: 'How does the story end? What did your hero learn?',
  }
  return prompts[stepType] || 'Write your story here...'
}
